import { type ReactNode } from 'react';
import { NavLink } from 'react-router';
import { useTranslation } from 'react-i18next';
import { BottomNav } from '@fluent/ui';
import { CardsIcon, HomeIcon, PhrasesIcon, TestIcon, UserIcon } from './icons.js';

/**
 * AppBottomNav — bottom tab bar for the signed-in surface area.
 *
 * Five tabs, left → right: Home (skill tree), Flashcards (SRS over learned
 * vocab), ტესტი (the national-exam simulator — it replaced Practice on
 * 2026-09-25), ფრაზები (the phrases course — it took Missions' slot on
 * 2026-10-18; Missions now opens from the Home card and Profile), Profile.
 * Wraps `@fluent/ui`'s `BottomNav` shell with router-aware `NavLink`s so
 * active state syncs with the current pathname. `/achievements` still
 * renders the bar, with no tab active.
 *
 * Each main screen renders this component; the screen body needs
 * `padding-bottom: ~88px` (or via the `Stack` it already uses) so content
 * doesn't sit under the bar.
 */

interface NavItem {
  to: string;
  labelKey: string;
  Icon: () => ReactNode;
  /** Maps to `data-color` on the item — feeds `--tab-color` / `--tab-color-dark`. */
  color: 'home' | 'cards' | 'practice' | 'phrases' | 'profile';
  exact: boolean;
}

const ITEMS: NavItem[] = [
  { to: '/',             labelKey: 'nav.home',         Icon: HomeIcon,     color: 'home',     exact: true  },
  { to: '/flashcards',   labelKey: 'nav.flashcards',   Icon: CardsIcon,    color: 'cards',    exact: false },
  { to: '/test',         labelKey: 'nav.practice',     Icon: TestIcon,     color: 'practice', exact: false },
  { to: '/phrases',      labelKey: 'nav.phrases',      Icon: PhrasesIcon,  color: 'phrases',  exact: false },
  { to: '/profile',      labelKey: 'nav.profile',      Icon: UserIcon,     color: 'profile',  exact: false },
];

interface AppBottomNavProps {
  /** Draw this tab as the active one whatever the URL says — for a preview
   *  route that stands in for a real screen (`/phrases-review` → `/phrases`). */
  activeTo?: string;
}

export function AppBottomNav({ activeTo }: AppBottomNavProps = {}) {
  const { t } = useTranslation();
  return (
    <BottomNav aria-label={t('nav.bottom_aria')}>
      {ITEMS.map(({ to, labelKey, Icon, color, exact }) => (
        <NavLink
          key={to}
          to={to}
          end={exact}
          data-color={color}
          className={({ isActive }) =>
            `fluent-bottomnav__item${(activeTo === undefined ? isActive : activeTo === to) ? ' is-active' : ''}`
          }
        >
          <span className="fluent-bottomnav__icon" aria-hidden="true">
            <Icon />
          </span>
          <span>{t(labelKey)}</span>
        </NavLink>
      ))}
    </BottomNav>
  );
}
